/**
 * Determines which sub-app should handle a given URL pathname.
 *
 * Derived from the redirect registries in both apps:
 *   - apps/studio-classic/next/pages/Routes.jsx  (createExternalRoute → studio5)
 *   - apps/studio5/src/routes/Routes.tsx          (RedirectToStudioClassic → classic)
 *
 * Studio-classic is the "legacy" catch-all: any path not explicitly claimed
 * by studio5 falls through to classic.
 */

const STUDIO5_EXACT_PATHS = new Set([
  '/accounts-v2',
  '/dev-tools',
  '/diagnostics-v2',
  '/email-performance',
  '/folders',
  '/insights-feed',
  '/performance',
]);

/**
 * Prefixes where the ENTIRE sub-tree belongs to studio5.
 */
const STUDIO5_FULL_PREFIXES = [
  '/apps/',
  '/email-sender/',
  '/emails/',
  '/experiments',
  '/goals',
  '/insights/funnels',
  '/integrations',
  '/journeys',
  '/push-notifications/',
  '/segments-v2/',
  '/settings/ai-assistant',
  '/settings/emails/',
  '/settings/identifier',
  '/settings/properties',
  '/settings/subscription-v2',
  '/settings/team',
  '/webhooks',
  '/workflows',
];

/**
 * Audience sub-paths owned by studio5.
 */
const STUDIO5_AUDIENCE_PREFIXES = [
  '/audience/accounts',
  '/audience/segments-v2',
  '/audience/users-v2',
];

/**
 * Experience types where studio5 owns the builder pages and
 * studio-classic owns listing, analytics, and settings pages.
 */
const BUILDER_PATTERN =
  /^\/(flows-v2|embeds|nps-v2|launchpads)\/(builder|[^/]+\/builder)(\/|$)/;

const LAUNCHPAD_ANALYTICS_PATTERN = /^\/launchpads\/[^/]+\/analytics(\/|$)/;

export function isStudio5Route(pathname: string): boolean {
  if (STUDIO5_EXACT_PATHS.has(pathname)) {
    return true;
  }

  if (STUDIO5_FULL_PREFIXES.some(p => pathname.startsWith(p))) {
    return true;
  }

  if (pathname === '/audience' || pathname === '/audience/') {
    return true;
  }

  if (STUDIO5_AUDIENCE_PREFIXES.some(p => pathname.startsWith(p))) {
    return true;
  }

  if (BUILDER_PATTERN.test(pathname)) {
    return true;
  }

  if (LAUNCHPAD_ANALYTICS_PATTERN.test(pathname)) {
    return true;
  }

  return false;
}
